/**
 * Bounded Damerau–Levenshtein edit distance (plan §6.2) — the metric under
 * every fuzzy correction.
 *
 * @remarks
 * Optimal String Alignment (OSA) variant: substitutions, insertions,
 * deletions, and adjacent transpositions each cost 1, and no substring is
 * edited twice. OSA is the standard typo metric (`tkae` → `take` is one
 * transposition) and stays cheap; the unrestricted-DL difference only appears
 * on pathological inputs no player types.
 *
 * Bounded: the caller supplies the maximum distance it cares about, and
 * anything beyond it returns `maximumDistance + 1` — a documented "beyond"
 * sentinel. Two prunes keep short-token comparison fast: a length-gap check
 * before any work, and a per-row early exit once no cell can recover to
 * within the bound.
 *
 * **Every DP read is total, because nothing is indexed.** A row travels as a
 * list of {@link DistanceColumn}s and is walked with `for…of`: the cell above
 * is the loop binding, the cell to the left is the one this loop just
 * produced, the diagonal is the previous iteration's cell above, and the
 * transposition source rides along on the column that already holds it.
 *
 * That is a correction. This module used to read cells through a
 * `row[columnIndex] ?? Number.POSITIVE_INFINITY` helper and claim here that
 * both of its branches were live — which was wrong. All four call sites were
 * provably in range (a row always holds one cell per prefix of `rightText`;
 * the transposition source is only wanted where the character test already
 * proved two characters exist on each side), so the fallback was dead code the
 * coverage gate could never reach. It is gone rather than suppressed, per
 * engineering_standards §14.1 — restructure so the invariant is type-visible,
 * never guard-and-ignore.
 *
 * Pure and deterministic: same pair + same bound ⇒ same number, always.
 *
 * @packageDocumentation
 */

// cspell:ignore tkae -- deliberate player typo: the transposition example above needs it

/**
 * One column of a completed DP row, carrying both values the NEXT row reads at
 * that column.
 *
 * @remarks
 * `cell` is that row's distance — the next row's deletion source. `transpositionSource`
 * is the cell two columns to the left in the row ABOVE this one, which is
 * exactly `d[i-2][j-2]` from the next row's point of view: the only value the
 * transposition arm can want, and the reason the DP needs no third row and no
 * indexing.
 */
interface DistanceColumn {
	readonly cell: number;
	/**
	 * `Number.POSITIVE_INFINITY` where no such cell exists (the first column, and
	 * every column of row 0) — an out-of-range cell IS an impossible transition,
	 * so the sentinel is semantics rather than a guard. The character test never
	 * reaches those columns anyway.
	 */
	readonly transpositionSource: number;
}

/**
 * Compute the OSA Damerau–Levenshtein distance between two folded words,
 * bounded above.
 *
 * @param leftText - One word (typically the player's token)
 * @param rightText - The other word (typically a vocabulary/catalog candidate)
 * @param maximumDistance - The largest distance the caller cares about
 * @returns The exact distance when it is ≤ `maximumDistance`; `maximumDistance + 1` otherwise
 */
export const boundedDamerauLevenshtein = (leftText: string, rightText: string, maximumDistance: number): number => {
	if (leftText === rightText) {
		return 0;
	}

	const beyondBound = maximumDistance + 1;

	if (Math.abs(leftText.length - rightText.length) > maximumDistance) {
		return beyondBound;
	}

	// Row 0: turning an empty `leftText` into `rightText`'s first j characters
	// costs j. Row 1 can never transpose — that needs two characters on each side
	// — so row 0 carries no real transposition sources.
	let previousColumns: readonly DistanceColumn[] = Array.from({ length: rightText.length }, (_, columnOffset) => ({
		cell: columnOffset + 1,
		transpositionSource: Number.POSITIVE_INFINITY,
	}));
	// d[0][rightText.length] — the answer while `leftText` is still empty. Every
	// completed row replaces it with its own last cell.
	let finalCell = rightText.length;

	for (let leftIndex = 1; leftIndex <= leftText.length; leftIndex += 1) {
		const leftCharCode = leftText.charCodeAt(leftIndex - 1);
		// charCodeAt returns NaN out of range, and NaN equals nothing — so the
		// transposition test self-disables at the top edge without any guard.
		const previousLeftCharCode = leftText.charCodeAt(leftIndex - 2);
		const currentColumns: DistanceColumn[] = [];

		// Column 0 of this row and of the row above: turning i characters into
		// nothing costs i.
		let leftCell = leftIndex; // d[i][j-1] — the cell this loop just produced
		let diagonalCell = leftIndex - 1; // d[i-1][j-1]
		let twoColumnsBack = Number.POSITIVE_INFINITY; // d[i-1][j-2] — no such cell at j = 1
		let rowMinimum = leftIndex;

		for (const [columnOffset, { cell: aboveCell, transpositionSource }] of previousColumns.entries()) {
			// `columnOffset` is the 0-based column, so it addresses characters
			// directly: j-1 is `columnOffset`, j-2 is `columnOffset - 1`.
			const rightCharCode = rightText.charCodeAt(columnOffset);
			const substitutionCost = leftCharCode === rightCharCode ? 0 : 1;
			// The same NaN algebra as above, on the left edge this time: a
			// transposition is only ever claimed with two characters on each side,
			// which is why `transpositionSource` is only read where a cell exists.
			const isTransposition =
				leftCharCode === rightText.charCodeAt(columnOffset - 1) && previousLeftCharCode === rightCharCode;
			const cellDistance = Math.min(
				aboveCell + 1, // deletion
				leftCell + 1, // insertion
				diagonalCell + substitutionCost, // substitution / match
				isTransposition ? transpositionSource + 1 : Number.POSITIVE_INFINITY,
			);

			// The source this column hands DOWN is a cell of the row above, two
			// columns left — which is `d[i-2][j-2]` once the next row reads it.
			currentColumns.push({ cell: cellDistance, transpositionSource: twoColumnsBack });
			rowMinimum = Math.min(rowMinimum, cellDistance);
			// Slide the window one column right: today's diagonal is tomorrow's
			// two-columns-back, and today's cell above is tomorrow's diagonal.
			leftCell = cellDistance;
			twoColumnsBack = diagonalCell;
			diagonalCell = aboveCell;
		}

		if (rowMinimum > maximumDistance) {
			return beyondBound;
		}

		// d[i][rightText.length] — or d[i][0] = i when `rightText` is empty and the
		// column walk never ran.
		finalCell = leftCell;
		previousColumns = currentColumns;
	}

	return finalCell > maximumDistance ? beyondBound : finalCell;
};
